import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { confidentialAPI } from '../services/api';
import { parseApiValidationErrors } from '../utils/apiErrors';
import toast from '../utils/toast';
import { localDateKey, selectDisplayedSalary } from '../utils/salaryDisplayUtils';

// Payroll periods (now using PayrollRun list_periods endpoint)
export const usePayrollPeriods = () => {
  return useQuery({
    queryKey: ['payroll', 'runs', 'periods'],
    queryFn: async () => {
      const response = await api.get('/confidential/payroll-runs/list_periods/');
      return response.data.results || [];
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Payroll runs
export const usePayrollRuns = () => {
  return useQuery({
    queryKey: ['payroll', 'runs'],
    queryFn: async () => {
      const response = await api.get('/confidential/payroll-runs/');
      return response.data.results || [];
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

const PAYROLL_BANK_FILE_CLASSIFICATIONS = ['worker', 'staff'];

/**
 * Load the two required payroll payment batches from the same bank adapter.
 * Keeping both previews visible prevents an operator from overlooking either
 * classification or accidentally treating a filtered total as the full run.
 */
export const usePayrollBankFilePreviews = (
  payrollRunId,
  bank = '',
  enabled = true,
) => {
  const results = useQueries({
    queries: PAYROLL_BANK_FILE_CLASSIFICATIONS.map((classification) => ({
      queryKey: [
        'payroll',
        'bank-file-preview',
        payrollRunId,
        classification,
        bank || 'default',
      ],
      queryFn: async () => {
        const response = await confidentialAPI.getPayrollBankFilePreview(
          payrollRunId,
          classification,
          bank,
        );
        return response.data;
      },
      enabled: enabled && !!payrollRunId,
      staleTime: 0,
      refetchOnMount: 'always',
    })),
  });

  return {
    previews: PAYROLL_BANK_FILE_CLASSIFICATIONS.reduce(
      (accumulator, classification, index) => ({
        ...accumulator,
        [classification]: results[index]?.data || null,
      }),
      {},
    ),
    isLoading: results.some((result) => result.isLoading),
    isFetching: results.some((result) => result.isFetching),
    error: results.find((result) => result.error)?.error || null,
    refetch: () => Promise.all(results.map((result) => result.refetch())),
  };
};

export const useDownloadPayrollBankFile = () => {
  return useMutation({
    mutationFn: async ({
      payrollRunId,
      classification,
      bank,
      branchCodesReviewed,
    }) => {
      const response = await confidentialAPI.downloadPayrollBankFile(
        payrollRunId,
        classification,
        bank,
        branchCodesReviewed,
      );
      return response;
    },
  });
};

export const usePayrollBankConfigurations = () => {
  return useQuery({
    queryKey: ['payroll', 'bank-configuration', 'list'],
    queryFn: async () => {
      const response = await confidentialAPI.getPayrollBankConfigurations();
      return response.data?.results || response.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });
};

export const useSavePayrollBankConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => (
      id
        ? confidentialAPI.updatePayrollBankConfiguration(id, data)
        : confidentialAPI.createPayrollBankConfiguration(data)
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['payroll', 'bank-configuration'],
      });
      queryClient.invalidateQueries({
        queryKey: ['payroll', 'bank-file-preview'],
      });
      toast.success('Bank file configuration saved');
    },
    onError: (error) => {
      toast.error(
        parseApiValidationErrors(error)
        || 'Failed to save bank file configuration',
      );
    },
  });
};

// Payroll statistics
export const usePayrollStatistics = () => {
  return useQuery({
    queryKey: ['payroll', 'statistics'],
    queryFn: async () => {
      const response = await api.get('/confidential/payroll-statistics/');
      return response.data || {};
    },
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Compliance reports
export const useComplianceReports = () => {
  return useQuery({
    queryKey: ['payroll', 'compliance'],
    queryFn: async () => {
      const response = await api.get('/confidential/payroll-compliance/');
      return response.data.results || [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};

// Payroll mutations
export const useCreatePayrollPeriod = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/confidential/payroll-runs/create_period/', data);
      return response.data; // Return the created PayrollRun data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs', 'periods'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      // Note: Don't show toast here - let the caller handle it for better UX flow
    },
    onError: (error) => {
      console.error('Error creating payroll period:', error);
      // Note: Don't show toast here - let the caller handle it for better UX flow
    },
  });
};

export const useProcessPayroll = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => api.post('/confidential/payroll-runs/', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'statistics'] });
      toast.success('Payroll processed successfully');
    },
    onError: (error) => {
      console.error('Error processing payroll:', error);
      toast.error('Failed to process payroll');
    },
  });
};

// Start payroll calculation (background task)
export const useRunPayrollCalculation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ payrollRunId, departmentId, forceRecalculate }) => {
      const response = await api.post(`/confidential/payroll-runs/${payrollRunId}/run_calculation/`, {
        department_id: departmentId,
        force_recalculate: forceRecalculate || false,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Don't invalidate yet - wait for task to complete
      toast.success('Payroll calculation started');
    },
    onError: (error) => {
      console.error('Error starting payroll calculation:', error);
      toast.error(error.response?.data?.error || 'Failed to start payroll calculation');
    },
  });
};

// Poll payroll calculation status
export const usePayrollCalculationStatus = (payrollRunId, taskId, enabled = false) => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['payroll', 'calculation-status', payrollRunId, taskId],
    queryFn: async () => {
      if (!payrollRunId || !taskId) return null;
      const response = await api.get(
        `/confidential/payroll-runs/${payrollRunId}/calculation_status/`,
        { params: { task_id: taskId } }
      );
      return response.data;
    },
    enabled: enabled && !!payrollRunId && !!taskId,
    refetchInterval: (query) => {
      const taskState = query.state.data?.state;
      if (taskState === 'SUCCESS' || taskState === 'FAILURE') {
        queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
        queryClient.invalidateQueries({ queryKey: ['payroll', 'statistics'] });
        queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
        queryClient.invalidateQueries({ queryKey: ['payroll', 'department-progress'] });
        return false;
      }
      return 2000;
    },
    staleTime: 0, // Always fetch fresh data
  });
};

// Calculate single employee payroll (queues Celery; callers that need the
// finished EP should poll calculation_status then invalidate — see Step 3
// detail-panel Recalc in PayrollManagement.js).
export const useCalculateSingleEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ payrollRunId, employeeId, forceRecalculate }) => {
      const response = await api.post(`/confidential/payroll-runs/${payrollRunId}/calculate_employee/`, {
        employee_id: employeeId,
        force_recalculate: forceRecalculate || false,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'department-progress'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'flags'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'step2_reconciliation'] });
    },
    onError: (error) => {
      console.error('Error calculating employee payroll:', error);
      toast.error(error.response?.data?.error || 'Failed to calculate employee payroll');
    },
  });
};

// Adjustment reason options for dropdown
export const ADJUSTMENT_REASONS = [
  { value: 'salary_correction', label: 'Salary Correction' },
  { value: 'backpay_claim', label: 'Backpay/Arrears' },
  { value: 'overtime_claim', label: 'Overtime Claim' },
  { value: 'leave_adjustment', label: 'Leave Adjustment' },
  { value: 'reimbursement', label: 'Expense Reimbursement' },
  { value: 'error_correction', label: 'Data Entry Error' },
  { value: 'bonus', label: 'Ad-hoc Bonus' },
  { value: 'allowance_adjustment', label: 'Allowance Adjustment' },
  { value: 'other', label: 'Other (Specify in Notes)' },
];

// Deduction type options for adjustment modal dropdown
export const DEDUCTION_ADJUSTMENT_TYPES = [
  { value: 'idd_charges', label: 'IDD Charges' },
  { value: 'global_sms', label: 'Global SMS' },
  { value: 'season_parking', label: 'Season Parking' },
  { value: 'salary_advance', label: 'Salary Advance Recovery' },
  { value: 'salary_overpayment', label: 'Salary Overpayment Recovery' },
  { value: 'training_bond', label: 'Training Bond' },
  { value: 'other', label: 'Other' },
];

const ADJUSTMENT_METADATA_FIELDS = {
  targetDate: 'target_date',
  dailyRate: 'daily_rate',
  monthlyBasicSalary: 'monthly_basic_salary',
  momWorkingDays: 'mom_working_days',
  workWeekType: 'work_week_type',
  rateBasis: 'rate_basis',
  rateSource: 'rate_source',
  sourcePayrollId: 'source_payroll_id',
  suggestedAmount: 'suggested_amount',
  correctedEntitlement: 'corrected_entitlement',
  amountAlreadyPaid: 'amount_already_paid',
  hoursWorked: 'hours_worked',
  entitlementType: 'entitlement_type',
  dayType: 'day_type',
  dayMultiplier: 'day_multiplier',
  tier: 'tier',
  otHours: 'ot_hours',
  otHourlyRate: 'ot_hourly_rate',
  calculationFingerprint: 'calculation_fingerprint',
  calculationVersion: 'calculation_version',
  calculationSummary: 'calculation_summary',
  requiresManualReview: 'requires_manual_review',
  isRateOverridden: 'is_rate_overridden',
  overrideReason: 'override_reason',
  wasLocked: 'was_locked',
  isCurrentlyLocked: 'is_currently_locked',
  amendedAfterLock: 'amended_after_lock',
  attendanceRecordId: 'attendance_record_id',
  sourceLockIds: 'source_lock_ids',
  clockEventIds: 'clock_event_ids',
  lockAppliedAt: 'lock_applied_at',
  amendmentTimestamp: 'amendment_timestamp',
  amendmentByStaffId: 'amendment_by_staff_id',
};

export const buildPayrollAdjustmentLineItem = (item, fallbackDescription) => {
  const payload = {
    description: item.description || fallbackDescription,
    amount: parseFloat(item.amount) || 0,
  };

  Object.entries(ADJUSTMENT_METADATA_FIELDS).forEach(([formKey, apiKey]) => {
    const value = item[formKey];
    if (value !== undefined && value !== null && value !== '') {
      payload[apiKey] = value;
    }
  });

  return payload;
};

// Resolve a selected prior date to its attendance-aware unpaid entitlement.
export const useBackpayPreview = () => {
  return useMutation({
    mutationKey: ['payroll', 'backpay-preview'],
    mutationFn: async ({ employeePayrollId, targetDate }) => {
      const response = await api.get(
        `/confidential/employee-payrolls/${employeePayrollId}/backpay-preview/`,
        { params: { target_date: targetDate } }
      );
      return response.data;
    },
  });
};

export const useBackpayRateLookup = useBackpayPreview;

// Preview payroll adjustment impact (debounced server-side calculation)
export const usePreviewAdjustment = () => {
  return useMutation({
    mutationFn: async ({ employeePayrollId, adjustmentData }) => {
      const response = await api.post(
        `/confidential/employee-payrolls/${employeePayrollId}/preview_adjustment/`,
        adjustmentData
      );
      return response.data;
    },
    onError: (error) => {
      console.error('Error previewing adjustment:', error);
      // Don't show toast for preview errors - just log them
    },
  });
};

// Adjust employee payroll (HR only - enhanced with line items)
export const useAdjustPayroll = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params) => {
      const { employeePayrollId, adjustmentData, bonusPayments, otherPayments, otherDeductions, adjustmentNotes } = params;
      
      // Support both old format (direct fields) and new format (adjustmentData object)
      let payload;
      
      if (adjustmentData) {
        const cleanItems = (items, fallbackDesc) =>
          (items || [])
            .filter(i => i.amount && parseFloat(i.amount))
            .map(i => buildPayrollAdjustmentLineItem(i, fallbackDesc));

        payload = {
          backpay_items: cleanItems(adjustmentData.backpayItems, 'Backpay'),
          overtime_items: cleanItems(adjustmentData.overtimeItems, 'Overtime'),
          allowance_items: cleanItems(adjustmentData.allowanceItems, 'Allowance'),
          bonus_items: cleanItems(adjustmentData.bonusItems, 'Bonus'),
          deduction_items: cleanItems(adjustmentData.deductionItems, 'Deduction'),
          reimbursement_items: cleanItems(adjustmentData.reimbursementItems, 'Reimbursement'),
          adjustment_notes: adjustmentData.adjustmentNotes || '',
          // Legacy fields for backward compatibility
          bonus_payments: adjustmentData.bonusPayments || 0,
          other_payments: adjustmentData.otherPayments || 0,
          other_deductions: adjustmentData.otherDeductions || 0,
        };
      } else {
        // Legacy format (backward compatibility)
        payload = {
          bonus_payments: bonusPayments || 0,
          other_payments: otherPayments || 0,
          other_deductions: otherDeductions || 0,
          adjustment_notes: adjustmentNotes || '',
        };
      }
      
      const response = await api.patch(
        `/confidential/employee-payrolls/${employeePayrollId}/adjust/`,
        payload
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payroll-detail'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      toast.success(`Adjustment saved for ${data.employee_name || data.employee_id}`);
    },
    onError: (error) => {
      console.error('Error adjusting payroll:', error);
      toast.error(error.response?.data?.error || 'Failed to save adjustment');
    },
  });
};

// Fetch employee payrolls for a specific payroll run
export const useEmployeePayrolls = (payrollRunId, enabled = true) => {
  return useQuery({
    queryKey: ['payroll', 'employee-payrolls', payrollRunId],
    queryFn: async () => {
      if (!payrollRunId) return [];
      const response = await api.get('/confidential/employee-payrolls/', {
        params: { payroll_run: payrollRunId, page_size: 50000 }
      });
      return response.data.results || response.data || [];
    },
    enabled: enabled && !!payrollRunId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Fetch individual employee payroll detail (includes ytd_summary, aw_ceiling_info, shg_contributions_display)
export const useEmployeePayrollDetail = (employeePayrollId) => {
  return useQuery({
    queryKey: ['payroll', 'employee-payroll-detail', employeePayrollId],
    queryFn: async () => {
      if (!employeePayrollId) return null;
      const response = await api.get(`/confidential/employee-payrolls/${employeePayrollId}/`);
      return response.data;
    },
    enabled: !!employeePayrollId,
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
};

// Fetch department progress for a payroll run
export const useDepartmentProgress = (payrollRunId, enabled = true) => {
  return useQuery({
    queryKey: ['payroll', 'department-progress', payrollRunId],
    queryFn: async () => {
      if (!payrollRunId) return null;
      const response = await api.get(`/confidential/payroll-runs/${payrollRunId}/department_progress/`);
      return response.data;
    },
    enabled: enabled && !!payrollRunId,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      if (query.state.data?.summary?.is_fully_calculated) {
        return false;
      }
      return 10000;
    },
  });
};

// Calculated rates for a specific employee (overtime, rest day rates)
export const useCalculatedRates = (staffId, year = null) => {
  return useQuery({
    queryKey: ['salary', 'calculated-rates', staffId, year],
    queryFn: async () => {
      if (!staffId) return null;
      
      try {
        // Build query params
        const params = { staff_id: staffId };
        if (year) {
          params.year = year;
        }
        
        // Fetch calculated rates from the API
        const response = await confidentialAPI.get('/confidential/payroll/calculated_rates_by_staff/', { params });
        return response.data || null;
      } catch (error) {
        // Don't throw error - just return null if not found or access denied
        if (error?.response?.status === 404 || error?.response?.status === 403) {
          return null;
        }
        console.error('Error fetching calculated rates:', error);
        return null;
      }
    },
    enabled: !!staffId,
    staleTime: 5 * 60 * 1000, // 5 minutes - rates change when salary changes
    retry: (failureCount, error) => {
      // Don't retry 404s (no salary record) or 403s (permission denied)
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

// Current active salary for a specific employee
export const useCurrentSalary = (staffId) => {
  return useQuery({
    queryKey: ['salary', 'current', staffId],
    queryFn: async () => {
      if (!staffId) return null;
      
      try {
        // Fetch current active salary records
        const response = await confidentialAPI.getEmployeeSalary({
          payroll_staff_id: staffId,
          payroll_is_active: true,
        });
        
        const salaries = response.data?.results || response.data || [];
        
        return selectDisplayedSalary(salaries, localDateKey());
      } catch (error) {
        // Don't throw error - just return null if salary not found or access denied
        if (error?.response?.status === 404 || error?.response?.status === 403) {
          return null;
        }
        console.error('Error fetching current salary:', error);
        return null;
      }
    },
    enabled: !!staffId,
    staleTime: 5 * 60 * 1000, // 5 minutes - salary data changes infrequently
    retry: (failureCount, error) => {
      // Don't retry 404s (no salary record) or 403s (permission denied)
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

// =============================================================================
// PAYROLL FLAGS (Exception-Based Review)
// =============================================================================

// Fetch payroll flags for a payroll run
export const usePayrollFlags = (payrollRunId, options = {}) => {
  const { groupBySeverity = true, enabled = true } = options;
  
  return useQuery({
    queryKey: ['payroll', 'flags', payrollRunId, { groupBySeverity }],
    queryFn: async () => {
      if (!payrollRunId) return null;
      const params = { 
        payroll_run: payrollRunId,
        group_by_severity: groupBySeverity 
      };
      const response = await api.get('/confidential/payroll-flags/', { params });
      return response.data;
    },
    enabled: enabled && !!payrollRunId,
    staleTime: 30 * 1000, // 30 seconds - refresh frequently during review
  });
};

// Live Step 2 ↔ Step 3 reconciliation for the run.
//
// Calls ``GET /confidential/payroll-runs/{id}/step2-reconciliation/``
// which (a) recomputes the AR / leave-session source-of-truth aggregates,
// (b) compares them against what Step 3 saved, and (c) syncs the
// persistent ``STEP2_DRIFT`` PayrollFlag rows so the existing
// "Continue to Step 4" gate (driven by ``flagSummary.can_proceed``)
// blocks until any detected drift is resolved.
//
// Returns ``{drift_rows, drifts, ...}``; consumers should typically
// only render UI when ``drift_rows > 0``.
export const useStep2Reconciliation = (payrollRunId, enabled = true) => {
  return useQuery({
    queryKey: ['payroll', 'step2_reconciliation', payrollRunId],
    queryFn: async () => {
      if (!payrollRunId) return null;
      const response = await api.get(
        `/confidential/payroll-runs/${payrollRunId}/step2-reconciliation/`,
        { params: { sync: true } }
      );
      return response.data;
    },
    enabled: enabled && !!payrollRunId,
    staleTime: 30 * 1000,
    // Self-heal if HR is mid-recalc: refetch every 15s while drift
    // exists; stop polling once the run is clean.
    refetchInterval: (query) => {
      if (!query.state.data) return false;
      return query.state.data.drift_rows > 0 ? 15000 : false;
    },
  });
};

// Fetch flag summary for a payroll run
export const usePayrollFlagSummary = (payrollRunId, enabled = true) => {
  return useQuery({
    queryKey: ['payroll', 'flags', 'summary', payrollRunId],
    queryFn: async () => {
      if (!payrollRunId) return null;
      const response = await api.get('/confidential/payroll-flags/summary/', {
        params: { payroll_run: payrollRunId }
      });
      return response.data;
    },
    enabled: enabled && !!payrollRunId,
    staleTime: 30 * 1000,
    refetchInterval: (query) => {
      if (query.state.data?.can_proceed) {
        return false;
      }
      return 10000;
    },
  });
};

// Mark a flag as reviewed
export const useReviewFlag = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ flagId, reviewNotes }) => {
      const response = await api.patch(`/confidential/payroll-flags/${flagId}/review/`, {
        review_notes: reviewNotes || '',
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate flag queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['payroll', 'flags'] });
      toast.success(`Flag reviewed: ${data.flag_type_display}`);
    },
    onError: (error) => {
      console.error('Error reviewing flag:', error);
      toast.error(error.response?.data?.error || 'Failed to review flag');
    },
  });
};

// Exclude employee from payroll run
export const useExcludeFromRun = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ flagId, exclusionReason }) => {
      const response = await api.patch(`/confidential/payroll-flags/${flagId}/exclude/`, {
        exclusion_reason: exclusionReason,
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate flag and payroll queries
      queryClient.invalidateQueries({ queryKey: ['payroll', 'flags'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      toast.success(`Employee excluded from payroll run`);
    },
    onError: (error) => {
      console.error('Error excluding employee:', error);
      toast.error(error.response?.data?.error || 'Failed to exclude employee');
    },
  });
};

// Bulk review multiple flags
export const useBulkReviewFlags = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ flagIds, reviewNotes }) => {
      const response = await api.post('/confidential/payroll-flags/bulk_review/', {
        flag_ids: flagIds,
        review_notes: reviewNotes || '',
      });
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate flag queries
      queryClient.invalidateQueries({ queryKey: ['payroll', 'flags'] });
      toast.success(`${data.count} flags reviewed`);
    },
    onError: (error) => {
      console.error('Error bulk reviewing flags:', error);
      toast.error(error.response?.data?.error || 'Failed to review flags');
    },
  });
};

// Auto-verify review flags against source data (Step 2 reconciliation,
// salary records, HR profile); human-only flag types are left untouched
export const useAutoVerifyFlags = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payrollRunId) => {
      const response = await api.post('/confidential/payroll-flags/auto_verify/', {
        payroll_run: payrollRunId,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'flags'] });
      if (data.verified_count > 0) {
        toast.success(`${data.verified_count} flag(s) auto-verified — ${data.remaining_count} left for review`);
      } else {
        toast(`No flags could be auto-verified — ${data.remaining_count} need human review`);
      }
    },
    onError: (error) => {
      console.error('Error auto-verifying flags:', error);
      toast.error(error.response?.data?.error || 'Failed to auto-verify flags');
    },
  });
};

// =============================================================================
// PAYROLL FINALIZATION (Lock/Unlock)
// =============================================================================

// Finalize a single employee payroll (lock it from recalculation)
export const useFinalizePayroll = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payrollId) => {
      const response = await api.post(`/confidential/employee-payrolls/${payrollId}/finalize/`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      toast.success(`Payroll finalized for ${data.employee_staff_id}`);
    },
    onError: (error) => {
      console.error('Error finalizing payroll:', error);
      toast.error(error.response?.data?.error || 'Failed to finalize payroll');
    },
  });
};

// Unfinalize a single employee payroll (unlock for recalculation - Finance/Admin only)
export const useUnfinalizePayroll = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ payrollId, reason }) => {
      const response = await api.post(`/confidential/employee-payrolls/${payrollId}/unfinalize/`, {
        reason,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      toast.success(`Payroll unfinalized for ${data.employee_staff_id}`);
    },
    onError: (error) => {
      console.error('Error unfinalizing payroll:', error);
      toast.error(error.response?.data?.error || 'Failed to unfinalize payroll');
    },
  });
};

// Bulk finalize multiple employee payrolls
export const useBulkFinalizePayrolls = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payrollIds) => {
      const response = await api.post('/confidential/employee-payrolls/bulk_finalize/', {
        payroll_ids: payrollIds,
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      const message = data.success_count > 0 
        ? `${data.success_count} payroll(s) finalized`
        : 'No payrolls finalized';
      if (data.already_finalized > 0) {
        toast.success(`${message} (${data.already_finalized} already finalized)`);
      } else {
        toast.success(message);
      }
    },
    onError: (error) => {
      console.error('Error bulk finalizing payrolls:', error);
      toast.error(error.response?.data?.error || 'Failed to finalize payrolls');
    },
  });
};

// PILOT: release payslips to workers (flips run to 'approved' + notifies workers only)
export const useReleasePayslips = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ payrollRunId, bank, bankFilesAuthorized }) => {
      const response = await api.post(
        `/confidential/payroll-runs/${payrollRunId}/release-payslips/`,
        {
          bank,
          bank_files_authorized: bankFilesAuthorized,
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      toast.success(`Payslips released — ${data.workers_notified} worker(s) notified`);
    },
    onError: (error) => {
      console.error('Error releasing payslips:', error);
      toast.error(error.response?.data?.error || 'Failed to release payslips');
    },
  });
};

// Bulk finalize ALL employee payrolls in a run (Step 3 "Lock All")
export const useBulkFinalizeRun = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payrollRunId) => {
      const response = await api.post(`/confidential/payroll-runs/${payrollRunId}/bulk-finalize/`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['payroll', 'employee-payrolls'] });
      if (data.finalized_count > 0) {
        toast.success(`${data.finalized_count} payroll record(s) locked`);
      } else {
        toast.success('All payroll records are already locked');
      }
    },
    onError: (error) => {
      console.error('Error bulk finalizing payroll run:', error);
      toast.error(error.response?.data?.error || 'Failed to lock payroll records');
    },
  });
};
